/**
 * ProactiveCue (SR2 Phase B.3 / P5.1) — the deterministic corner-cue cards.
 *
 * Renders the server-computed proactive suggestions (checkup incomplete, tasks
 * blocked, care pending, docs expiring) shown when the Companion opens. Copy is
 * localized client-side from the card's i18n key + params (the proactive path
 * never ships prose). Dismiss = don't repeat (server-enforced via the dedupe
 * index). Rendered at the top of the panel; a floating out-of-panel cue is a
 * later placement refinement.
 *
 * Reading and computing are separate calls (F52). The cards on screen come
 * from `GET /copilot/proactive`, a pure read. New cues are computed by
 * `POST /copilot/proactive/evaluate`, which this cue asks for once when it
 * opens and again whenever its tab becomes visible; the SERVER decides how
 * often that actually computes (inside its window it returns the stored cards).
 * The GET used to compute, so every cue load was a write.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@tonnie/locale';
import { CoPilotService, useApiClient } from '@tonnie/sdk';
import { Button, cn } from '@tonnie/ui';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';

const PROACTIVE_KEY = ['copilot', 'proactive'] as const;

interface IProactiveCueProps {
  /** Only fetch when the panel is open. */
  enabled: boolean;
}

export function ProactiveCue({ enabled }: IProactiveCueProps) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const client = useApiClient();
  const service = useMemo(() => new CoPilotService(client), [client]);
  const queryClient = useQueryClient();

  const { data: cards } = useQuery({
    queryKey: PROACTIVE_KEY,
    queryFn: () => service.getProactive(),
    enabled,
    staleTime: 60_000,
  });

  const evaluate = useMutation({
    mutationFn: () => service.evaluateProactive(),
    onSuccess: (fresh) => queryClient.setQueryData(PROACTIVE_KEY, fresh),
  });
  const { mutate: requestEvaluation } = evaluate;

  // Ask the server to compute when the cue opens, and each time the tab comes
  // back into view. The server paces the computation, not this component.
  useEffect(() => {
    if (!enabled) return;
    requestEvaluation();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') requestEvaluation();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled, requestEvaluation]);

  const dismiss = useMutation({
    mutationFn: (id: string) => service.dismissProactive(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROACTIVE_KEY }),
  });

  // Mark the surfaced cues seen once (engagement) — fire-and-forget.
  useEffect(() => {
    cards?.forEach((c) => void service.markProactiveSeen(c.suggestionId).catch(() => undefined));
  }, [cards, service]);

  if (!cards || cards.length === 0) return null;

  return (
    <div className="px-4 pt-3 space-y-2" data-testid="copilot-proactive">
      {cards.map((card) => (
        <div
          key={card.suggestionId}
          className={cn(
            'flex items-start justify-between gap-2 rounded-lg border border-border',
            'bg-primary/5 px-3 py-2 text-sm'
          )}
        >
          <button
            type="button"
            className="text-left text-foreground hover:underline"
            onClick={() => card.deepLink && navigate(card.deepLink)}
            disabled={!card.deepLink}
          >
            {t(card.titleKey, { ...card.params })}
          </button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 shrink-0 px-2 text-xs"
            onClick={() => dismiss.mutate(card.suggestionId)}
            aria-label={t('copilot.proactive.dismiss')}
          >
            ✕
          </Button>
        </div>
      ))}
    </div>
  );
}
